import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import type { FieldProps } from 'formik';
import { Field } from 'formik';
import React from 'react';

import InputLabel from '@components/InputLabel';

type PickedAutocompleteProps<T> = Pick<
  AutocompleteProps<T, true, false, false>,
  | 'disableCloseOnSelect'
  | 'getOptionDisabled'
  | 'getOptionLabel'
  | 'isOptionEqualToValue'
  | 'options'
  | 'renderOption'
  | 'renderTags'
>;

type AsyncAutocompleteProps<T> = Pick<
  AutocompleteProps<T, true, false, false>,
  'onClose' | 'onOpen' | 'open' | 'loading'
>;

interface FormikCustomMultiAutocompleteFieldProps<
  T,
> extends PickedAutocompleteProps<T> {
  name: string;
  label?: string;
  required?: boolean;
  onValueChange?: (value: T[]) => string[];
  async?: AsyncAutocompleteProps<T>;
}

export default function FormikCustomMultiAutocompleteField<T>({
  label,
  name,
  options,
  required = false,
  onValueChange,
  async,
  disableCloseOnSelect,
  getOptionDisabled,
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  renderTags,
}: FormikCustomMultiAutocompleteFieldProps<T>) {
  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps<T[]>) => {
        const handleChange = (
          event: React.SyntheticEvent<Element, Event>,
          value: T[]
        ) => {
          event.preventDefault();
          event.stopPropagation();
          const mappedValues = onValueChange?.(value);
          if (mappedValues) {
            /**
             * Use case: Options are {user_id, name}[], but want to only store user_id[]
             * onValueChange transforms value input into string[] of user_ids
             */
            form.setFieldValue(field.name, mappedValues, true);
          } else {
            /**
             * Use case: Options are {user_id, name}[] and want to store as is
             */
            form.setFieldValue(field.name, value, true);
          }
        };

        return (
          <Autocomplete
            {...async}
            disableCloseOnSelect={disableCloseOnSelect}
            multiple
            onChange={handleChange}
            /*
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              e.stopPropagation();
            }}
              */
            onBlur={field.onBlur}
            getOptionDisabled={getOptionDisabled}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            options={options}
            renderInput={(params) => (
              <>
                {label && (
                  <InputLabel
                    label={label}
                    required={required}
                    style={{ color: 'primary.main' }}
                  />
                )}
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    className: meta.error && meta.touched ? 'error' : undefined,
                    endAdornment: (
                      <React.Fragment>
                        {async?.loading ? (
                          <CircularProgress
                            color="inherit"
                            size={20}
                            sx={{ mr: 3 }}
                          />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  error={Boolean(meta.error && meta.touched)}
                  name={field.name}
                  /*
                  onClick={(
                    e: React.MouseEvent<HTMLDivElement, MouseEvent>
                  ) => {
                    e.stopPropagation();
                  }}
                  */
                  helperText={
                    meta.touched && meta.error ? meta.error : undefined
                  }
                  sx={{
                    padding: 1,
                  }}
                  variant="outlined"
                />
              </>
            )}
            size="small"
            renderOption={renderOption}
            renderTags={
              renderTags
                ? (value, getTagProps, ownerState) => {
                    return (
                      <div
                        style={{
                          width: '2000px',
                          maxHeight: 150,
                          overflowY: 'auto',
                        }}
                      >
                        {renderTags(value, getTagProps, ownerState)}
                      </div>
                    );
                  }
                : undefined
            }
            sx={{ maxHeight: 150 }}
            value={field.value}
          />
        );
      }}
    </Field>
  );
}
